import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import { DATA_ROOM_DOCS } from './communicationsData';

const ACCESS_BADGE = {
  Restricted: 'bg-red-50 text-red-700',
  Confidential: 'bg-amber-50 text-amber-700',
  Internal: 'bg-[rgba(59,76,76,0.07)] text-blue-700',
  Public: 'bg-emerald-50 text-emerald-700',
};

function FileIcon({ type }) {
  const config = {
    pdf: { icon: 'FileText', color: 'text-red-600', bg: 'bg-red-50' },
    xlsx: { icon: 'FileSpreadsheet', color: 'text-emerald-600', bg: 'bg-emerald-50' },
    docx: { icon: 'FileText', color: 'text-[#3b4c4c]', bg: 'bg-[rgba(59,76,76,0.07)]' },
  };
  const c = config?.[type] || config?.pdf;
  return (
    <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${c?.bg} flex-shrink-0`}>
      <Icon name={c?.icon} size={15} className={c?.color} />
    </div>
  );
}

export default function DocumentSharingModal({ onClose, onAttach }) {
  const [tab, setTab] = useState('dataroom');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadDone, setUploadDone] = useState(false);
  const [fileName, setFileName] = useState('');
  const [category, setCategory] = useState('');
  const [accessLevel, setAccessLevel] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const filteredDocs = DATA_ROOM_DOCS?.filter(d =>
    d?.name?.toLowerCase()?.includes(search?.toLowerCase()) ||
    d?.category?.toLowerCase()?.includes(search?.toLowerCase())
  );

  const toggleSelect = (doc) => {
    setSelected(prev =>
      prev?.find(d => d?.id === doc?.id) ? prev?.filter(d => d?.id !== doc?.id) : [...prev, doc]
    );
  };

  const handleUpload = () => {
    if (!fileName?.trim()) return;
    setUploading(true);
    setUploadProgress(0);
    const interval = setInterval(() => {
      setUploadProgress(p => {
        if (p >= 100) {
          clearInterval(interval);
          setUploading(false);
          setUploadDone(true);
          return 100;
        }
        return p + 20;
      });
    }, 300);
  };

  const handleAttach = () => {
    onAttach(selected);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl mx-4 overflow-hidden flex flex-col" style={{ maxHeight: '85vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Attach Document</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors">
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-5 pt-3 pb-0">
          {[{ id: 'dataroom', label: 'From Data Room' }, { id: 'upload', label: 'Upload New' }]?.map(t => (
            <button
              key={t?.id}
              onClick={() => setTab(t?.id)}
              className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors border-b-2 ${
                tab === t?.id
                  ? 'border-[#3b4c4c] text-[#3b4c4c] bg-[#3b4c4c]/4'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {t?.label}
            </button>
          ))}
        </div>
        <div className="h-px bg-slate-200 mx-5" />

        <div className="flex-1 overflow-y-auto p-5">
          {tab === 'dataroom' && (
            <div className="space-y-3">
              <div className="relative">
                <Icon name="Search" size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search documents…"
                  value={search}
                  onChange={e => setSearch(e?.target?.value)}
                  className="w-full pl-8 pr-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#3b4c4c]/20"
                />
              </div>
              <div className="space-y-1.5">
                {filteredDocs?.map(doc => {
                  const isSelected = !!selected?.find(d => d?.id === doc?.id);
                  return (
                    <div
                      key={doc?.id}
                      onClick={() => toggleSelect(doc)}
                      className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                        isSelected ? 'border-[#3b4c4c]/30 bg-[#3b4c4c]/4' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(doc)}
                        className="accent-[#3b4c4c] flex-shrink-0"
                        onClick={e => e?.stopPropagation()}
                      />
                      <FileIcon type={doc?.type} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{doc?.name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-slate-500">{doc?.category}</span>
                          <span className="text-xs px-1.5 py-0.5 rounded bg-[#3b4c4c]/8 text-[#3b4c4c] font-medium">{doc?.version}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${ACCESS_BADGE?.[doc?.accessLevel] || 'bg-slate-100 text-slate-600'}`}>{doc?.accessLevel}</span>
                        </div>
                      </div>
                      <span className="text-xs text-slate-400 flex-shrink-0">{doc?.size}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {tab === 'upload' && (
            <div className="space-y-4">
              {/* Drop zone */}
              <div
                onDragOver={e => { e?.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e?.preventDefault(); setDragOver(false); }}
                className={`border-2 border-dashed rounded-2xl p-8 text-center transition-colors ${
                  dragOver ? 'border-[#3b4c4c] bg-[#3b4c4c]/4' : 'border-slate-300 hover:border-slate-400'
                }`}
              >
                <Icon name="Upload" size={28} className="text-slate-400 mx-auto mb-2" />
                <p className="text-sm font-medium text-slate-600 mb-1">Drag and drop files here</p>
                <p className="text-xs text-slate-400 mb-3">or</p>
                <button className="px-4 py-2 rounded-lg bg-[#3b4c4c] text-white text-sm font-medium hover:bg-[#162d4a] transition-colors">
                  Browse Files
                </button>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">File Name</label>
                <input
                  type="text"
                  value={fileName}
                  onChange={e => setFileName(e?.target?.value)}
                  placeholder="Enter file name…"
                  className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#3b4c4c]/20"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Category</label>
                  <select
                    value={category}
                    onChange={e => setCategory(e?.target?.value)}
                    className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none"
                  >
                    <option value="">Select…</option>
                    <option>Technical</option>
                    <option>Financial</option>
                    <option>Legal</option>
                    <option>ESG</option>
                    <option>Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Access Level</label>
                  <select
                    value={accessLevel}
                    onChange={e => setAccessLevel(e?.target?.value)}
                    className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none"
                  >
                    <option value="">Select…</option>
                    <option>Public</option>
                    <option>Internal</option>
                    <option>Confidential</option>
                    <option>Restricted</option>
                  </select>
                </div>
              </div>

              {uploading && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-slate-600">Uploading…</span>
                    <span className="text-xs font-medium text-[#3b4c4c]">{uploadProgress}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-[#3b4c4c] transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {uploadDone && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-50 border border-emerald-200">
                  <Icon name="CheckCircle" size={14} className="text-emerald-600" />
                  <span className="text-xs font-medium text-emerald-700">Document uploaded and attached</span>
                </div>
              )}

              <button
                onClick={handleUpload}
                disabled={!fileName?.trim() || uploading}
                className={`w-full py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  fileName?.trim() && !uploading
                    ? 'bg-[#3b4c4c] text-white hover:bg-[#162d4a]'
                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                }`}
              >
                {uploading ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          )}
        </div>

        {/* Selected chips */}
        {selected?.length > 0 && (
          <div className="px-5 py-3 border-t border-slate-100 bg-slate-50">
            <p className="text-xs font-semibold text-slate-500 mb-2">Selected ({selected?.length})</p>
            <div className="flex flex-wrap gap-1.5">
              {selected?.map(doc => (
                <span key={doc?.id} className="flex items-center gap-1 px-2 py-1 rounded-full bg-[#3b4c4c]/8 text-[#3b4c4c] text-xs font-medium">
                  {doc?.name?.length > 30 ? doc?.name?.slice(0, 30) + '…' : doc?.name}
                  <button onClick={() => toggleSelect(doc)} className="hover:text-red-500 transition-colors">
                    <Icon name="X" size={10} />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-slate-200">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors">
            Cancel
          </button>
          <button
            onClick={handleAttach}
            disabled={selected?.length === 0}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selected?.length > 0
                ? 'bg-[#3b4c4c] text-white hover:bg-[#162d4a]'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            }`}
          >
            <Icon name="Paperclip" size={14} />
            Attach {selected?.length > 0 ? `(${selected?.length})` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}
